import { batch } from 'react-redux';
import type { NavigateFunction } from 'react-router-dom';
import {
  appendUsers,
  resetCheckListSelections,
  setActiveChecklist,
  setChecklistItems,
  setChecklists,
  setChecklistSummary,
  setGroups,
  setIsCommentSending,
  setIsGroupsLoading,
  setIsLoading,
  setIsUsersLoading,
  setScrollToChecklistItemId,
  setUsers,
} from '.';
import type { StartChecklistGroupsAndUsersParams } from '../../../apis/checklistsAPI';
import {
  addComment,
  changeItemStatus,
  checklistContinue,
  createNewChecklist,
  deleteTemplate,
  editChecklist,
  endChecklist,
  getChecklist,
  getChecklistItems,
  getChecklists,
  getChecklistsSummary,
  renameChecklist,
  saveItems,
  shareChecklist,
  shareChecklistWithUsers,
  startChecklist,
  startChecklistGroupsAndUsers,
  unshareChecklist,
  unShareChecklistWithUsers,
} from '../../../apis/checklistsAPI';
import type { GetChecklistProps } from '../../../apis/checklistsAPI/types';
import { GetChecklistType } from '../../../apis/checklistsAPI/types';
import { getGroups } from '../../../apis/groupsAPI';
import type { LegacyGetGroupsQuery } from '../../../apis/groupsAPI/types';
import type { GetUserQuery } from '../../../apis/userAPI';
import { getAllUsers } from '../../../apis/userAPI';
import type { AppThunk } from '../../../store';
import type { Checklist, ChecklistSharedUser, ChecklistTask } from './types';
import { fetchTenant } from '../../Organizations/OrganizationsSlice/actionCreators';
import {
  initialChecklistPagingValues,
  defaultCheckListFiltersByStatus,
  mappedChecklistType,
} from '../Checklists.models';
import type { Group } from '../../GroupsList/groupsSlice/types';
import { getGroupById, isGroupSuccessData } from '../../../api/groups';
import type { SelectListUser } from '../../CreateMessage/createMessageSlice/types';

type CreateChecklistParams = {
  list: {
    sortIndex: number;
    name: string;
  }[];
  userIds: number[];
  groupIds: number[];
  onSuccess: () => void;
  checklistName: string;
};
type EditChecklistParams = {
  checklistID: number;
  selectedGroupIds: number[];
  unselectedGroupsIds: number[];
  selectedUsersIds: number[];
  unSelectedUsersIds: number[];
  onSuccess: () => void;
};

export const fetchUsersFromChecklistAndMerge =
  (query: GetUserQuery, checklist: Checklist, onlySelected = false): AppThunk =>
  async dispatch => {
    try {
      dispatch(setIsUsersLoading(true));

      const selectedUsers = transformChecklistUsersToSelectListUsers(
        checklist.directUserShares,
        checklist.userSharesViaGroups
      );

      const allUsersByQuery = await getAllUsers(query);

      // Upgrade selected users with full data if available in query results
      const enrichedSelectedUsers = selectedUsers.map(
        selected => allUsersByQuery.find(u => u.id === selected.id) || selected
      );

      // Add remaining users from query (not selected) unless onlySelected=true
      const remainingUsers = onlySelected
        ? []
        : allUsersByQuery.filter(u => !selectedUsers.find(s => s.id === u.id));

      const users = [...enrichedSelectedUsers, ...remainingUsers];

      batch(() => {
        dispatch(setUsers(users));
        dispatch(setIsUsersLoading(false));
      });
    } catch (error) {
      console.log('error log ', error);
      dispatch(setIsUsersLoading(false));
    }
  };

const transformChecklistUsersToSelectListUsers = (
  directShares: ChecklistSharedUser[],
  viaGroupShares: ChecklistSharedUser[]
): SelectListUser[] => {
  const allUsers = [...directShares, ...viaGroupShares];

  // Deduplicate by ID
  const uniqueUsers = Object.values(Object.fromEntries(allUsers.map(user => [user.id, user])));

  return uniqueUsers.map(user => ({
    id: user.id,
    displayName: user.name,
    // The checklist UI expects title and photoFileName, we can add that in the backend as a progressive enhancement.
    title: '',
    photoFileName: null,
    email: '',
    available: true,
    creatorId: null,
    unavailable: false,
    unavailableDateTo: null,
  }));
};

export const fetchUsers =
  (query?: GetUserQuery): AppThunk =>
  async dispatch => {
    try {
      dispatch(setIsUsersLoading(true));
      const users = await getAllUsers(query);
      batch(() => {
        dispatch(setUsers(users));
        dispatch(setIsUsersLoading(false));
      });
    } catch (error) {
      console.log('error log ', error);
      dispatch(setIsUsersLoading(false));
    }
  };

export const fetchAdditionalUsersByPage =
  (query?: GetUserQuery): AppThunk<Promise<number>> =>
  async dispatch => {
    try {
      dispatch(setIsUsersLoading(true));
      const additionalUsers = await getAllUsers(query);
      batch(() => {
        dispatch(appendUsers(additionalUsers));
        dispatch(setIsUsersLoading(false));
      });
      return additionalUsers.length;
    } catch (error) {
      console.log('error log ', error);
      dispatch(setIsUsersLoading(false));
      return 0;
    }
  };

export const fetchGroups =
  (query?: LegacyGetGroupsQuery, groupIds?: number[], shouldNotLoadAllGroups = false): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsGroupsLoading(true));

      let tenant = getState().organizations.tenant;
      if (!tenant) {
        await dispatch(fetchTenant());
        tenant = getState().organizations.tenant;
        if (!tenant) {
          throw new Error('Missing tenant');
        }
      }
      const tenantId = tenant.id;

      let groups: Group[] = [];
      if (groupIds && shouldNotLoadAllGroups) {
        const result = await Promise.all(
          groupIds.map(groupId => getGroupById({ groupId, tenantId }))
        );

        if (result.some(data => data.error)) {
          throw new Error();
        }

        groups = result
          .filter(isGroupSuccessData)
          .map(res => ({ ...res.data.data, type: res.data.data.groupType })); // TODO: Remove groupType/type mapping when migrating GET groups to core-api
      } else {
        groups = await getGroups(query);
        if (groupIds?.length) {
          groupIds.forEach(groupId => {
            const foundGroupIndex = groups.findIndex(g => g.id === groupId);
            const groupData = { ...groups[foundGroupIndex] };
            if (foundGroupIndex > -1) {
              groups.splice(foundGroupIndex, 1);
              groups.unshift(groupData);
            }
          });
        }
      }
      batch(() => {
        dispatch(setGroups(groups));
        dispatch(setIsGroupsLoading(false));
      });
    } catch (error) {
      console.log('error log ', error);
      dispatch(setIsGroupsLoading(false));
    }
  };

export const fetchChecklists =
  (
    props?: GetChecklistProps,
    isIntervalFetch = { is: false, resetSkip: () => {} },
    isPaging = false
  ): AppThunk =>
  async (dispatch, getState) => {
    const defaultProps = {
      ...initialChecklistPagingValues,
      type: [GetChecklistType.active],
    };
    const prevChecklists = getState().checklists.checklists;

    try {
      if (!isPaging && !isIntervalFetch.is) {
        dispatch(setIsLoading(true));
      }

      if (isIntervalFetch.is && prevChecklists) {
        const newChecklists = await getChecklists(
          props ? { ...props, skip: 0, limit: prevChecklists.length } : defaultProps
        );
        if (prevChecklists.length !== newChecklists.length) return;
        for (let index = 0; index < prevChecklists.length; index++) {
          if (prevChecklists[index].id !== newChecklists[index].id) {
            dispatch(setChecklists(newChecklists));
            isIntervalFetch.resetSkip();
            break;
          }
        }
        return;
      }

      const newChecklists = await getChecklists(props ?? defaultProps);

      batch(() => {
        dispatch(setChecklists(isPaging ? [...prevChecklists, ...newChecklists] : newChecklists));
        dispatch(setIsLoading(false));
      });
    } catch (error) {
      console.log('error log ', error);
      dispatch(setIsLoading(false));
    }
  };

const saveChecklistItems =
  (id: number, items: { name: string; sortIndex: number }[], onSuccess: () => void): AppThunk =>
  async (dispatch, getState) => {
    try {
      const res = await saveItems(id, items);
      if (res) {
        const activeTab = getState().checklists.activeTab;
        dispatch(
          fetchChecklists({
            ...initialChecklistPagingValues,
            ...defaultCheckListFiltersByStatus(activeTab),
            type: [mappedChecklistType[activeTab]],
          })
        );
        onSuccess();
      }
    } catch (error) {
      console.log('error log ', error);
    }
  };

export const createChecklist =
  (params: CreateChecklistParams): AppThunk =>
  async dispatch => {
    const { groupIds, list, onSuccess, userIds, checklistName } = params;
    try {
      const res = await createNewChecklist(checklistName, userIds, groupIds);
      if (res) {
        dispatch(saveChecklistItems(res.id, list, onSuccess));
      }
    } catch (error) {
      console.log('error log ', error);
    }
  };

export const changeChecklistItemStatus =
  (id: number, itemId: number, completed: boolean): AppThunk =>
  async (dispatch, getState) => {
    try {
      const res = await changeItemStatus(id, itemId, completed);
      if (res) {
        const newItems = [...getState().checklists.checklistItems];
        const foundIndex = newItems.findIndex(e => e.id === itemId);
        newItems[foundIndex] = res;

        dispatch(setChecklistItems(newItems));
      }
    } catch (error) {
      console.log('error log ', error);
    }
  };

export const fetchChecklistItems =
  (id: number, silentUpdate = false): AppThunk =>
  async dispatch => {
    if (!silentUpdate) dispatch(setIsLoading(true));
    try {
      const checklistItems = await getChecklistItems(id);
      if (checklistItems) {
        batch(() => {
          if (!silentUpdate) dispatch(setIsLoading(false));
          dispatch(setChecklistItems(checklistItems));
        });
      }
    } catch (error) {
      console.log('error log ', error);

      if (!silentUpdate) dispatch(setIsLoading(false));
    }
  };

export const sendComment =
  (id: number, itemID: number, model: { text: string | null }): AppThunk =>
  async dispatch => {
    try {
      dispatch(setIsCommentSending(true));
      dispatch(setScrollToChecklistItemId(itemID));
      const res = await addComment(id, itemID, model);
      if (res) {
        dispatch(fetchChecklistItems(id));
      }
    } catch (error) {
      console.log('error log ', error);
    } finally {
      dispatch(setIsCommentSending(false));
    }
  };

export const fetchChecklistSummary =
  (searchText: string | null, selectedFilters: GetChecklistProps): AppThunk =>
  async dispatch => {
    try {
      const summary = await getChecklistsSummary(searchText, selectedFilters);
      dispatch(setChecklistSummary(summary));
    } catch (error) {
      console.log('error fetchChecklistSummary ', error);
    }
  };

export const fetchOneChecklist =
  (id: number, callBack?: () => void): AppThunk =>
  async dispatch => {
    try {
      const checklist = await getChecklist(id);
      dispatch(setActiveChecklist(checklist));

      callBack?.();
    } catch (error) {
      console.log('error log ', error);
    }
  };

export const renameAChecklist =
  (id: number, newName: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));
      await renameChecklist(id, newName);
      const activeTab = getState().checklists.activeTab;

      batch(() => {
        dispatch(setIsLoading(false));
        dispatch(
          fetchChecklists({
            ...initialChecklistPagingValues,
            ...defaultCheckListFiltersByStatus(activeTab),
            type: [mappedChecklistType[activeTab]],
          })
        );
        dispatch(fetchOneChecklist(Number(id)));
      });
    } catch (error) {
      console.log('error log ', error);

      dispatch(setIsLoading(false));
    }
  };

export const fetchEndChecklist =
  (id: number, navigate: NavigateFunction): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));
      await endChecklist(id);
      const activeTab = getState().checklists.activeTab;

      batch(() => {
        dispatch(setIsLoading(false));
        dispatch(
          fetchChecklists({
            ...initialChecklistPagingValues,
            ...defaultCheckListFiltersByStatus(activeTab),
            type: [mappedChecklistType[activeTab]],
          })
        );
        navigate(`/checklist/${id}`);
        dispatch(fetchOneChecklist(Number(id)));
      });
    } catch (error) {
      console.log('error log ', error);

      dispatch(setIsLoading(false));
    }
  };

export const fetchDeleteTemplate =
  (id: number): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));
      await deleteTemplate(id);
      const activeTab = getState().checklists.activeTab;

      batch(() => {
        dispatch(setIsLoading(false));
        dispatch(
          fetchChecklists({
            ...initialChecklistPagingValues,
            ...defaultCheckListFiltersByStatus(activeTab),
            type: [mappedChecklistType[activeTab]],
          })
        );
      });
    } catch (error) {
      console.log('error log', error);
      dispatch(setIsLoading(false));
    }
  };

export const fetchContinueChecklist =
  (id: number, onSuccess: () => void): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));
      await checklistContinue(id);
      const activeTab = getState().checklists.activeTab;

      batch(() => {
        dispatch(setIsLoading(false));
        dispatch(
          fetchChecklists({
            ...initialChecklistPagingValues,
            ...defaultCheckListFiltersByStatus(activeTab),
            type: [mappedChecklistType[activeTab]],
          })
        );
      });
      onSuccess();
    } catch (error) {
      console.log('error log', error);
      dispatch(setIsLoading(false));
    }
  };

export const startChecklistAction =
  (id: number, params: StartChecklistGroupsAndUsersParams, navigate: NavigateFunction): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setIsLoading(true));
      const res =
        params.userIds?.length !== 0
          ? await startChecklistGroupsAndUsers(id, params)
          : await startChecklist(id, params.groupIds, params.newName);
      if (res) {
        const activeTab = getState().checklists.activeTab;
        dispatch(
          fetchChecklists({
            ...initialChecklistPagingValues,
            ...defaultCheckListFiltersByStatus(activeTab),
            type: [mappedChecklistType[activeTab]],
          })
        );
        navigate(`/checklist/${res.id}`);
      }
      dispatch(setIsLoading(false));
    } catch (error) {
      console.log('error log', error);
      dispatch(setIsLoading(false));
    }
  };

const shareChecklistWithGroups =
  (id: number, groupIDs: number[], onSuccess: () => void): AppThunk =>
  dispatch => {
    dispatch(setIsLoading(true));
    const promises: unknown[] = [];
    groupIDs.forEach(groupID => {
      promises.push(
        // eslint-disable-next-line no-async-promise-executor
        new Promise(async (resolve, reject) => {
          try {
            const res = await shareChecklist(id, groupID);
            resolve(res);
          } catch (error) {
            console.log(`error sharing with group: ${groupID}. Error: ${error}`);
            reject();
          }
        })
      );
    });
    Promise.allSettled(promises)
      .then(() => {
        dispatch(setIsLoading(false));
        onSuccess();
      })
      .catch(err => console.log(err));
  };

const unshareChecklistWithGroups =
  (id: number, groupIDs: number[], onSuccess: () => void): AppThunk =>
  dispatch => {
    dispatch(setIsLoading(true));
    const promises: unknown[] = [];
    groupIDs.forEach(groupID => {
      promises.push(
        // eslint-disable-next-line no-async-promise-executor
        new Promise(async (resolve, reject) => {
          try {
            const res = await unshareChecklist(id, groupID);
            resolve(res);
          } catch (error) {
            console.log(`error sharing with group: ${groupID}. Error: ${error}`);
            reject();
          }
        })
      );
    });
    Promise.allSettled(promises)
      .then(() => {
        onSuccess();
        dispatch(setIsLoading(false));
      })
      .catch(err => console.log(err));
  };

export const editChecklistItems =
  (checklistID: number, items: ChecklistTask[], onSuccess: () => void): AppThunk =>
  async dispatch => {
    try {
      await editChecklist(checklistID, items);
      onSuccess();
    } catch (error) {
      console.log('error log', error);
      dispatch(setIsLoading(false));
    }
    onSuccess();
  };

export const editChecklistGroups =
  (params: EditChecklistParams): AppThunk =>
  async (dispatch, getState) => {
    const {
      checklistID,
      onSuccess,
      selectedGroupIds,
      unselectedGroupsIds,
      selectedUsersIds,
      unSelectedUsersIds,
    } = params;
    let resolved = 0;
    dispatch(setIsLoading(true));

    const handleResolved = () => {
      resolved++;
      if (resolved === 2) {
        const activeTab = getState().checklists.activeTab;

        batch(() => {
          dispatch(
            fetchChecklists({
              ...initialChecklistPagingValues,
              ...defaultCheckListFiltersByStatus(activeTab),
              type: [mappedChecklistType[activeTab]],
            })
          );
          dispatch(fetchGroups({ menuitem: 'checklists' }));
          dispatch(resetCheckListSelections());
        });
        dispatch(setIsLoading(false));
        onSuccess();
      }
    };
    await Promise.all([
      selectedUsersIds.length && shareChecklistWithUsers(checklistID, selectedUsersIds),
      unSelectedUsersIds.length && unShareChecklistWithUsers(checklistID, unSelectedUsersIds),
    ]);
    dispatch(unshareChecklistWithGroups(checklistID, unselectedGroupsIds, handleResolved));
    dispatch(shareChecklistWithGroups(checklistID, selectedGroupIds, handleResolved));
  };

export const handleChecklistDataNotification =
  (checklistId: number): AppThunk =>
  async (dispatch, getState) => {
    try {
      const checklist = getState().checklists.activeChecklist;
      if (checklist?.id === checklistId) {
        batch(() => {
          dispatch(fetchOneChecklist(Number(checklistId)));
          dispatch(fetchChecklistItems(Number(checklistId), true));
        });
      }
    } catch (error) {
      console.log('error log', error);
    }
  };
